import * as React from "react";
import {
  Tabs as TabsPrimitive,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shared/ui/components/ui/tabs";
import { cn } from "@/shared/lib/utils";

export interface TabItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
}

export interface TabsProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive>,
    "children"
  > {
  items: TabItem[];
  variant?:
    | "grouped-filled"
    | "grouped-outline"
    | "underline"
    | "underline-bordered"
    | "individual";
  theme?: "dark" | "default";
  className?: string;
  listClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

const getVariantStyles = (
  variant:
    | "grouped-filled"
    | "grouped-outline"
    | "underline"
    | "underline-bordered"
    | "individual" = "grouped-filled",
  theme: "dark" | "default" = "default"
) => {
  const base = {
    "grouped-filled": {
      list: "inline-flex h-11 items-center justify-center rounded-lg bg-grey-50 py-1 px-3 gap-1",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 gap-2",
      triggerBase: "bg-transparent text-grey-300 h-9 px-5 py-2",
      triggerActive:
        "data-[state=active]:bg-grey-800 data-[state=active]:text-white data-[state=active]:font-bold",
      triggerHover: "hover:bg-grey-50",
    },
    "grouped-outline": {
      list: "inline-flex h-11 items-center justify-center rounded-lg bg-white border border-grey-100 shadow-[0_1px_4px_0_rgba(0,0,0,0.1)] py-2 px-3 ",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap text-xs font-medium focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 gap-2",
      triggerBase:
        "bg-transparent text-grey-300 py-1 border-r last:border-r-0 border-grey-100",
      triggerActive:
        "data-[state=active]:bg-grey-50 data-[state=active]:text-grey-800 data-[state=active]:font-bold h-10 rounded-none",
      triggerHover: "hover:bg-grey-50",
    },
    underline: {
      list: "inline-flex items-center justify-center gap-6",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap px-1 py-2 text-xs font-medium transition-all focus-visible:outline-none  disabled:pointer-events-none disabled:opacity-50 gap-2 rounded-none border-b-2 border-transparent",
      triggerBase: "text-grey-300",
      triggerActive:
        "data-[state=active]:text-grey-800 data-[state=active]:border-b-2 data-[state=active]:border-grey-800 data-[state=active]:font-bold",
      triggerHover: "hover:text-grey-900 hover:border-grey-300",
    },
    "underline-bordered": {
      list: "inline-flex items-center justify-center  gap-6 rounded-none",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap px-3 py-2 text-xs font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 gap-2 rounded-none border-b-2 border-transparent",
      triggerBase: "text-grey-300 bg-transparent",
      triggerActive:
        "data-[state=active]:text-grey-800 data-[state=active]:bg-white data-[status=active]:focus-visible:outline-none data-[state=active]:border-b-2 data-[state=active]:border-grey-800 data-[state=active]:font-bold",
      triggerHover: "hover:text-grey-900",
    },
    individual: {
      list: "inline-flex items-center justify-center gap-2",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2",
      triggerBase: "bg-transparent text-grey-300",
      triggerActive:
        "data-[state=active]:bg-grey-800 data-[state=active]:text-white data-[state=active]:font-bold",
      triggerHover: "hover:bg-grey-100",
    },
  };

  const dark = {
    "grouped-filled": {
      list: "bg-grey-800",
      triggerBase: "text-grey-300",
      triggerActive:
        "data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:font-bold",
      triggerHover: "hover:bg-grey-700",
    },
    "grouped-outline": {
      list: "inline-flex h-11 items-center justify-center rounded-lg bg-grey-50 border border-grey-200 py-2 px-3 gap-1",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap rounded-none text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2",
      triggerBase: "bg-transparent text-grey-300 py-1",
      triggerActive:
        "data-[state=active]:bg-grey-800 data-[state=active]:text-white data-[state=active]:font-bold",
      triggerHover: "hover:bg-grey-50",
    },
    underline: {
      list: "border-grey-700",
      triggerBase: "text-grey-400",
      triggerActive:
        "data-[state=active]:text-white data-[state=active]:border-white data-[state=active]:font-bold",
      triggerHover: "hover:text-grey-200 hover:border-grey-500",
    },
    "underline-bordered": {
      list: "border-grey-700",
      triggerBase: "text-grey-400 bg-transparent",
      triggerActive:
        "data-[state=active]:text-white data-[state=active]:font-bold data-[state=active]:bg-grey-800 data-[state=active]:border-b-0",
      triggerHover: "hover:text-grey-200",
    },
    individual: {
      list: "inline-flex items-center justify-center gap-2",
      trigger:
        "inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2",
      triggerBase: "bg-transparent text-grey-700",
      triggerActive:
        "data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-bold",
      triggerHover: "hover:bg-grey-100",
    },
  };

  const variantKey = variant || "grouped-filled";
  const baseStyles = base[variantKey];
  const themeStyles = theme === "dark" ? dark[variantKey] : undefined;

  return {
    list: cn(baseStyles.list, themeStyles?.list),
    trigger: cn(
      baseStyles?.trigger,
      baseStyles?.triggerBase,
      themeStyles?.triggerBase,
      baseStyles.triggerActive,
      themeStyles?.triggerActive,
      baseStyles.triggerHover,
      themeStyles?.triggerHover
    ),
  };
};

export const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive>,
  TabsProps
>(
  (
    {
      items,
      variant = "grouped-filled",
      theme = "default",
      className,
      listClassName,
      triggerClassName,
      contentClassName,
      ...props
    },
    ref
  ) => {
    const styles = getVariantStyles(variant, theme);

    return (
      <TabsPrimitive ref={ref} className={cn(className)} {...props}>
        <TabsList className={cn(styles.list, listClassName)}>
          {items.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className={cn(styles.trigger, triggerClassName)}
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {items.map((item) => (
          <TabsContent
            key={item.value}
            value={item.value}
            className={cn(contentClassName)}
          >
            {item.content}
          </TabsContent>
        ))}
      </TabsPrimitive>
    );
  }
);

Tabs.displayName = "Tabs";
